import { Alert, Card, Grid, IconButton, Slide, Snackbar, SnackbarCloseReason, Tooltip } from '@mui/material';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import moment from 'moment';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import useCurrentUser from 'src/hooks/use-current-user';
import toast from 'react-hot-toast';
import {
  getAllCodesAction,
  getAllICDAction,
  getAllSubmittedDocument,
  getAllTemplates,
  resetHoldDailyNoteFromBillingAction,
  resetSendMailToPhysician,
  resetUndoDocumentSignAction,
  resetreAssignTherapistAction,
} from 'src/redux/actions/documentationAction';
import { switchTherapistDisciplineAction } from 'src/redux/actions/patientAction';
import { useScopeChecker } from 'src/rbac';
import { useFeatureToggleWhenReady } from 'src/hooks/use-feature-toggle';
import { complianceOnBehalf } from 'src/config';
import {
  COMPLIANCE_DOCUMENT_ON_BEHALF_SCOPE,
  isComplianceCreateMode,
} from './compliance-document-on-behalf';
import CompletedDocument from './completed-document-list';
import EocDetailModal from './eoc-detail-modal';
import SignaturePendingList from './signature-pending-list';
import ViewDocumentList from './view-documents-list';
import { resolveDefaultDisciplineTab } from './clinical-discipline';
import { Check } from '@mui/icons-material';

export const tabs = [
  {
    label: 'PT',
    value: 'PT',
    icon: <Check />,
  },
  {
    label: 'OT',
    value: 'OT',
    icon: <Check />,
  },
  {
    label: 'SLP',
    value: 'SLP',
    icon: <Check />,
  },
];


const checkIconStyles = {
  borderRadius: '30px',
  background: '#6366F1',
  color: '#FFF',
  padding: '4px',
  '& .MuiSvgIcon-root': {
    fontSize: '16px',
  },
  '&:hover': {
    background: '#6366F1',
  },
};

const Documentation = ({
  openPrintMode,
  documentationDoc,
  setDocumentationDoc,
  selectedDate,
  setIsFilterApply,
  handleCLearAllDoc,
  isPatientInfoMissing,
}: any) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const switchTabs = (e: any, value: string) => {
    abortControllerCPTRef?.current?.abort();
    abortControllerIcdCodesRef?.current?.abort();
    abortControllerGetAllTemplateRef?.current?.abort();
    abortControllerScheduledDocumentRef?.current?.abort();
    abortControllerGetAllSubmissionRef?.current?.abort();
    openPrintMode && handleCLearAllDoc();
    dispatch(switchTherapistDisciplineAction(value));
  };
  const callSubmitEventReducer = useSelector((state: any) => state.callSubmitEventReducer);
  const [providerId, discipline] = useCurrentUser(['providerId', 'discipline']);
  const undoDocumentSignResponse = useSelector((state: any) => state.undoDocumentSignReducer);
  const holdFromBillingResponse = useSelector(
    (state: any) => state.holdDailyNoteFromBillingReducer
  );
  const activeTab = useSelector((state: any) => state.switchDisciplineReducer);
  const sendMailPhysicianResponse = useSelector((state: any) => state.sendMailToPhysicianReducer);
  const [getCPTcodes, setCPTcodes] = useState([]);
  const patientData = useSelector((state: any) => state?.getSinglePatientReducer?.patient);
  const getAllCodesResponse = useSelector((state: any) => state.getAllCodeReducers);
  const reAssigneResponse = useSelector((state: any) => state.reAssigntherapistReducer);
  const abortControllerGetAllSubmissionRef = useRef<any>();
  const abortControllerGetAllTemplateRef = useRef<any>();
  const abortControllerIcdCodesRef = useRef<any>();
  const abortControllerCPTRef = useRef<any>();
  const abortControllerScheduledDocumentRef = useRef<any>();
  const getAllSignaturePending = useSelector((state: any) => state.getAllSubmittedDocumentReducer);
  const getAllCompletedDoc = useSelector((state: any) => state.getAllCompetedDocReducer);
  const planOfCareDetails = useSelector(
    (state: any) => state?.getPlanOfCareReducer?.response?.data
  );


  const isCheckActive = (value: string) => {
    if (value === 'PT') return planOfCareDetails?.checkPtActive;
    if (value === 'OT') return planOfCareDetails?.checkOtActive;
    if (value === 'SLP') return planOfCareDetails?.checkSlpActive;
    return false;
  };

  useEffect(() => {
    if (!activeTab || !patientData) return;
    abortControllerCPTRef.current = new AbortController();
    abortControllerIcdCodesRef.current = new AbortController();

    dispatch(
      getAllCodesAction(
        {
          discipline: activeTab,
          type: 'cpt',
          signal: abortControllerCPTRef.current.signal,
          eocUuid: location?.state?.episodeOfCareId,
        },
        patientData?.uuid
      )
    );
    dispatch(
      getAllICDAction(
        {
          discipline: activeTab,
          type: 'icd',
          signal: abortControllerIcdCodesRef.current.signal,
          eocUuid: location?.state?.episodeOfCareId,
        },
        patientData?.uuid
      )
    );
  }, [activeTab, patientData]);

  useEffect(() => {
    if (!activeTab) return;
    abortControllerGetAllTemplateRef.current = new AbortController();

    dispatch(
      getAllTemplates({
        type: activeTab,
        episodeOfCareId: location?.state?.episodeOfCareId,
        signal: abortControllerGetAllTemplateRef.current.signal,
      })
    );
  }, [activeTab]);

  useEffect(() => {
    if (!getAllCodesResponse.areGettingCodes && getAllCodesResponse.response === null) return;
    setCPTcodes(getAllCodesResponse.response?.data?.cptCodes);
  }, [getAllCodesResponse]);

  const callGetAllSubmittedDocument = () => {
    if (!activeTab) return;
    abortControllerGetAllSubmissionRef.current = new AbortController();

    dispatch(
      getAllSubmittedDocument({
        therapistUuid: providerId,
        eocId: location?.state?.episodeOfCareId,
        signal: abortControllerGetAllSubmissionRef.current.signal,
        disciplineChange: true,
        params: {
          type: activeTab,
          filter: 'PENDING',
          sortDirection: 'desc',
          sortBy: 'dateOfService',
          page: 0,
          pageSize: 30,
        },
      })
    );
  };

  useEffect(() => {
    if (openPrintMode && selectedDate?.startDate && selectedDate?.endDate) {
      abortControllerGetAllSubmissionRef.current = new AbortController();
      dispatch(
        getAllSubmittedDocument({
          therapistUuid: providerId,
          eocId: location?.state?.episodeOfCareId,
          signal: abortControllerGetAllSubmissionRef.current.signal,
          disciplineChange: true,
          params: {
            type: activeTab,
            filter: 'PENDING',
            startDate: moment(selectedDate?.startDate).format('YYYY-MM-DD'),
            endDate: moment(selectedDate?.endDate).format('YYYY-MM-DD'),
          },
        })
      );
    }
  }, [openPrintMode, selectedDate]);

  useEffect(() => {
    callGetAllSubmittedDocument();
  }, [callSubmitEventReducer]);

  useEffect(() => {
    if (!activeTab) return;
    callGetAllSubmittedDocument();
  }, [activeTab]);

  const [open, setOpen] = useState(false);

  const handleClose = (event?: React.SyntheticEvent | Event, reason?: SnackbarCloseReason) => {
    if (reason === 'clickaway') {
      return;
    }

    setOpen(false);
  };

  useEffect(() => {
    if (!sendMailPhysicianResponse.sendMail && sendMailPhysicianResponse.response === null) return;
    callGetAllSubmittedDocument();
    setOpen(true);
    dispatch(resetSendMailToPhysician());
  }, [sendMailPhysicianResponse]);

  useEffect(() => {
    if (!undoDocumentSignResponse.isSuccess && undoDocumentSignResponse.response === null) return;
    callGetAllSubmittedDocument();
    dispatch(resetUndoDocumentSignAction());
  }, [undoDocumentSignResponse]);

  useEffect(() => {
    if (holdFromBillingResponse.loading || holdFromBillingResponse.response === null) return;
    if (holdFromBillingResponse.isSuccess) {
      toast.success(
        holdFromBillingResponse.hold ? 'Note held from billing.' : 'Note included in billing.'
      );
    } else {
      toast.error(
        holdFromBillingResponse.response?.message ||
          'Could not update the billing status of this note.'
      );
    }
    dispatch(resetHoldDailyNoteFromBillingAction());
  }, [holdFromBillingResponse]);

  const has = useScopeChecker();
  const [isComplianceOnBehalf] = useFeatureToggleWhenReady(complianceOnBehalf, 'ENTERPRISE');
  //  BRK-2074: a discipline-less COMPLIANCE_DOCUMENT_ON_BEHALF holder needs a working tab —
  //  the strip is a static PT/OT/SLP array and every per-tab fetch guards on activeTab, so
  //  without a default the page is inert for them. Fail-closed: legacy discipline-less users
  //  (no scope, or the Split off/loading) keep today's empty tab byte-identical.
  const complianceCreateMode = isComplianceCreateMode({
    complianceSplitOn: isComplianceOnBehalf,
    hasDocumentScope: has(COMPLIANCE_DOCUMENT_ON_BEHALF_SCOPE),
  });

  // THIN-8023: a non-clinical viewer (discipline NOT_APPLICABLE) has no discipline of their own to
  // open on, and seeding the tab with it sent `discipline=NOT_APPLICABLE` to every discipline-keyed
  // call on this page. The episode of care decides the default tab for them instead.
  useEffect(() => {
    if (activeTab !== '') return;
    const defaultTab = resolveDefaultDisciplineTab(discipline, planOfCareDetails);
    if (defaultTab) {
      dispatch(switchTherapistDisciplineAction(defaultTab));
      return;
    }
    // The episode of care has not loaded yet, so THIN-8023 has no tab to offer. BRK-2074 still
    // needs the compliance-on-behalf holder to land somewhere usable rather than on an inert page.
    if (complianceCreateMode) {
      dispatch(switchTherapistDisciplineAction('PT'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discipline, planOfCareDetails, complianceCreateMode]);

  useEffect(() => {
    if (!reAssigneResponse.isReAssign && reAssigneResponse.response === null) return;
    callGetAllSubmittedDocument();
    dispatch(resetreAssignTherapistAction());
  }, [reAssigneResponse]);

  const SlideTransition = useCallback((props: any) => {
    return (
      <Slide
        {...props}
        direction="left"
      />
    );
  }, []);

  return (
    <Box>
      <Grid
        display={'flex'}
        alignItems={'center'}
        marginBottom={1}
      >
        <Typography variant="h5">Plan Of Care & Documentation</Typography>
        <Tabs
          indicatorColor="primary"
          textColor="primary"
          value={activeTab}
          sx={{ px: 3 }}
          onChange={(e, value) => switchTabs(e, value)}
        >
          {tabs.map((tab) => (
            <Tab
              key={tab.value}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {tab.label}
                  {isCheckActive(tab.value) && (
                    <Tooltip placement="top" title='Active' >
                      <IconButton sx={checkIconStyles}>
                        <Check />
                      </IconButton>
                    </Tooltip>
                  )}

                </Box>
              }
              value={tab.value}
            />
          ))}
        </Tabs>
      </Grid>
      <Card>
        <EocDetailModal
          activeTab={activeTab}
          getCPTcodes={getCPTcodes}
        />
        <ViewDocumentList
          activeTab={activeTab}
          abortControllerScheduledDocumentRef={abortControllerScheduledDocumentRef}
          openPrintMode={openPrintMode}
          isPatientInfoMissing={isPatientInfoMissing}
        />
        <SignaturePendingList
          activeTab={activeTab}
          requireDocuments={getAllSignaturePending?.response?.content}
          openPrintMode={openPrintMode}
          documentationDoc={documentationDoc}
          setDocumentationDoc={setDocumentationDoc}
          setIsFilterApply={setIsFilterApply}
          abortController={abortControllerGetAllSubmissionRef?.current?.signal}
        />
        <CompletedDocument
          activeTab={activeTab}
          completedDocuments={getAllCompletedDoc?.response?.content}
          abortController={abortControllerGetAllSubmissionRef?.current?.signal}
          openPrintMode={openPrintMode}
          documentationDoc={documentationDoc}
          setDocumentationDoc={setDocumentationDoc}
          setIsFilterApply={setIsFilterApply}
        />
      </Card>
      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        open={open}
        autoHideDuration={2200}
        onClose={handleClose}
        TransitionComponent={SlideTransition}
      >
        <Alert
          onClose={handleClose}
          severity="success"
          variant="standard"
        >
          Signature request sent to physician successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Documentation;
